import * as Sentry from '@sentry/react';
import React from 'react';

import {ApiUserProfile} from 'src/lib/api/auth';
import {Project, SeasonalSeriesType, Site} from 'src/lib/api/project';
import {ForecastTimeSeriesQuery, SeasonalModelVisualMode, Source} from 'src/lib/api/series';
import {isPublicCustomerProject} from 'src/lib/siteUtils';
import {isUpstreamInternalOrgUser} from 'src/lib/userUtils';

import Seasonal2 from './Seasonal2';
import Seasonal3 from './Seasonal3';

type SeasonalReleaseModel = 'Seasonal-2' | 'Seasonal-3' | null;

export default function Seasonal({
  site,
  seriesType: seasonalSeriesType,
  project,
  defaultDateRange: defaultDateRangeProp,
  currentUser,
  includeFlowContributingAreas,
  setIncludeFlowContributingAreas,
}: {
  site: Site;
  seriesType: SeasonalSeriesType;
  project: Project;
  defaultDateRange?: [Date, Date];
  currentUser: ApiUserProfile | null;
  includeFlowContributingAreas: boolean;
  setIncludeFlowContributingAreas: (value: boolean) => void;
}) {
  const [isStaging, setIsStaging] = React.useState(false);

  // If we don't have any forecastTimeSeries for this plot being returned by the API, silently skip the seasonal viz but log a helpful error.
  if (site.forecastTimeSeries[seasonalSeriesType].length === 0) {
    const errorMessage = `series_type ${seasonalSeriesType} is set for site ${site.name} (${site.id}), but no seasonal forecast sources are being returned by the API. We are silently skipping any seasonal viz in the frontend, so no error should be apparent to the user; please remove this series_type from the site config, or create expected seasonal forecasts.`;
    // sites that are under construction or hidden may not be fully set up, but this isn't something
    // we need to alert about.
    if (isPublicCustomerProject(project)) {
      console.error(errorMessage);
      Sentry.captureException(new Error(errorMessage), {
        tags: {
          component: 'Seasonal',
          siteId: site.id,
          seasonalSeriesType,
        },
        extra: {
          siteName: site.name,
          availableSeriesTypes: Object.keys(site.forecastTimeSeries),
        },
      });
    }
  }

  // for the "release" model (the main plot we should show)
  const seasonalSeries = site.forecastTimeSeries[seasonalSeriesType].find(
    (series) => series.source === Source.HydroForecastSeasonal
  );

  const releaseModel: SeasonalReleaseModel = (() => {
    if (
      (seasonalSeries?.sourceMetadata &&
        seasonalSeries.sourceMetadata.modelGeneration === 'Seasonal-3') ||
      isStaging
    ) {
      // for now, always assume if we're in staging that we should display Seasonal-3 as the "release"
      return 'Seasonal-3';
    } else if (seasonalSeries) {
      return 'Seasonal-2';
    } else {
      // possible to have no releaseModel if we only have Seasonal-3 Preview forecasts
      return null;
    }
  })();
  // possible additional seasonal-3 series (if available, will be shown in "preview mode")
  const seasonal3CustomerPreviewSeries: ForecastTimeSeriesQuery | undefined =
    site.forecastTimeSeries[seasonalSeriesType].find(
      (series) => series.source === Source.HydroForecastSeasonal3
    );
  const seasonal3InternalPreviewSeries: ForecastTimeSeriesQuery | undefined =
    site.forecastTimeSeries[seasonalSeriesType].find(
      (series) => series.source === Source.HydroForecastSeasonal3InternalPreview
    );

  // There are many possible combinations of Seasonal-2 & Seasonal-3 forecasts, with specific rules for what supercedes what in priority.
  const seasonalModelVisualMode: SeasonalModelVisualMode = (() => {
    if (releaseModel === 'Seasonal-2') {
      if (seasonal3CustomerPreviewSeries) {
        // if the release model is Seasonal-2, and there is also an additional seasonal series for Seasonal-3,
        // we should show the Seasonal-2 plots plus the Seasonal-3 plots with the "customer preview mode" styling.
        return SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3CustomerPreview;
      } else if (seasonal3InternalPreviewSeries && isUpstreamInternalOrgUser(currentUser)) {
        // If the release model is Seasonal-2, and there is also an additional seasonal series for seasonal-3-internal-preview,
        // we should show the Seasonal-2 plots, and show the Seasonal-3 plots with the "internal preview mode" styling to
        // internal org users.
        return SeasonalModelVisualMode.Seasonal2ReleaseWithSeasonal3InternalPreview;
      } else {
        // if the release model is Seasonal-2, we should show the Seasonal-2 plots with the "release mode" styling.
        return SeasonalModelVisualMode.Seasonal2Release;
      }
    } else if (releaseModel === 'Seasonal-3') {
      // if the release model is Seasonal-3, we should show the Seasonal-3 plots with the "release mode" styling.
      return SeasonalModelVisualMode.Seasonal3Release;
    } else if (releaseModel === null) {
      if (seasonal3CustomerPreviewSeries) {
        // if there is no release model, but there are  Seasonal-3,
        // we should show the Seasonal-3 plots with the "customer preview mode" styling.
        return SeasonalModelVisualMode.Seasonal3CustomerPreviewWithNoReleaseModel;
      } else if (seasonal3InternalPreviewSeries && isUpstreamInternalOrgUser(currentUser)) {
        return SeasonalModelVisualMode.Seasonal3InternalPreviewWithNoReleaseModel;
      } else {
        // don't show a seasonal plot at all, with no indication something is amiss.
        // possible when:
        // - we would otherwise be in Seasonal3InternalPreviewWithNoReleaseModel, but user is not internal.
        // - we have no seasonal forecast sources being returned by the API.
        return SeasonalModelVisualMode.SilentlyShowNothing;
      }
    } else {
      throw new Error('Invalid seasonal release model');
    }
  })();

  if (seasonalModelVisualMode === SeasonalModelVisualMode.SilentlyShowNothing) {
    return <></>;
  }
  return (
    <>
      <Seasonal3
        site={site}
        seriesType={seasonalSeriesType}
        defaultDateRange={defaultDateRangeProp}
        currentUser={currentUser}
        isStaging={isStaging}
        setIsStaging={setIsStaging}
        seasonalModelVisualMode={seasonalModelVisualMode}
      />
      <Seasonal2
        site={site}
        seriesType={seasonalSeriesType}
        basinAveragingEnabled={project.basinAveragingEnabled}
        defaultDateRange={defaultDateRangeProp}
        currentUser={currentUser}
        isStaging={isStaging}
        setIsStaging={setIsStaging}
        seasonalModelVisualMode={seasonalModelVisualMode}
        includeFlowContributingAreas={includeFlowContributingAreas}
        setIncludeFlowContributingAreas={setIncludeFlowContributingAreas}
      />
    </>
  );
}
